/**
 * User Journey Mapping and Tracking System
 * Maps complete user paths for personalization and behavior analysis
 */

interface JourneyStep {
  page: string;
  timestamp: number;
  timeSpent: number;
  actions: string[];
  context?: any;
}

interface UserJourney {
  sessionId: string;
  userId?: string;
  startTime: number;
  steps: JourneyStep[];
  currentStep?: string;
  source: string;
  device: 'mobile' | 'tablet' | 'desktop';
  userType?: 'new' | 'returning' | 'trial' | 'paid';
}

// Helper function to determine device type
const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// Helper function to generate session ID
const generateSessionId = (): string => {
  return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

// Helper function to determine user type
const determineUserType = (journey: JourneyStep[]): 'new' | 'returning' | 'trial' | 'paid' => {
  const hasTrialIndicators = journey.some(step => 
    step.page.includes('trial') || step.actions.includes('trial_signup')
  );
  const hasPaidIndicators = journey.some(step => 
    step.page.includes('checkout') || step.actions.includes('purchase')
  );
  const isReturning = localStorage.getItem('user_return_visit') === 'true';
  
  if (hasPaidIndicators) return 'paid';
  if (hasTrialIndicators) return 'trial';
  if (isReturning) return 'returning';
  return 'new';
};

// Track page visits automatically
const trackPageView = (page: string, context?: any) => {
  if (typeof window !== 'undefined' && window.omnisend) {
    window.omnisend.push(['track', 'page_view', {
      page,
      context,
      timestamp: new Date().toISOString(),
      device: getDeviceType(),
      referrer: document.referrer
    }]);
  }
};

/**
 * Main journey tracking utilities
 */
export const JourneyTracker = {
  // Initialize journey tracking
  startJourney: (source: string, userId?: string) => {
    const sessionId = generateSessionId();
    const journey: UserJourney = {
      sessionId,
      userId,
      startTime: Date.now(),
      steps: [],
      source,
      device: getDeviceType()
    };
    
    sessionStorage.setItem('current_journey', JSON.stringify(journey));
    sessionStorage.setItem('page_start_time', Date.now().toString());
    
    // Mark as returning visitor for future visits
    localStorage.setItem('user_return_visit', 'true');
    
    trackPageView('journey_started', { source, sessionId });
  },

  // Track page navigation
  trackPageSequence: (newPage: string, context?: any) => {
    const currentJourneyStr = sessionStorage.getItem('current_journey');
    if (!currentJourneyStr) {
      // Initialize if not exists
      JourneyTracker.startJourney('direct');
      return;
    }
    
    const journey: UserJourney = JSON.parse(currentJourneyStr);
    const previousPageStartTime = parseInt(sessionStorage.getItem('page_start_time') || '0');
    const timeSpent = previousPageStartTime ? Date.now() - previousPageStartTime : 0;
    
    // Add current page as a step
    const step: JourneyStep = {
      page: newPage,
      timestamp: Date.now(),
      timeSpent: Math.round(timeSpent / 1000), // Convert to seconds
      actions: [],
      context
    };
    
    journey.steps.push(step);
    journey.currentStep = newPage;
    journey.userType = determineUserType(journey.steps);
    
    // Update storage
    sessionStorage.setItem('current_journey', JSON.stringify(journey));
    sessionStorage.setItem('page_start_time', Date.now().toString());
    
    // Track the page sequence update
    if (typeof window !== 'undefined' && window.omnisend) {
      window.omnisend.push(['track', 'page_sequence_updated', {
        journey_length: journey.steps.length,
        current_page: newPage,
        user_type: journey.userType,
        session_id: journey.sessionId,
        time_spent_on_previous: step.timeSpent
      }]);
    }
    
    trackPageView(newPage, context);
  },

  // Track user actions on current page
  trackAction: (action: string, details?: any) => {
    const currentJourneyStr = sessionStorage.getItem('current_journey');
    if (!currentJourneyStr) return;
    
    const journey: UserJourney = JSON.parse(currentJourneyStr);
    if (journey.steps.length === 0) return;
    
    // Add action to current step
    const currentStep = journey.steps[journey.steps.length - 1];
    currentStep.actions.push(action);
    
    if (details) {
      currentStep.context = { ...currentStep.context, [action]: details };
    }
    
    // Update storage
    sessionStorage.setItem('current_journey', JSON.stringify(journey));
    
    // Track action
    if (typeof window !== 'undefined' && window.omnisend) {
      window.omnisend.push(['track', 'user_action', {
        action,
        details,
        page: currentStep.page,
        session_id: journey.sessionId,
        step_number: journey.steps.length
      }]);
    }
  },

  // Track feature interest/interaction
  trackFeatureInterest: (feature: string, interactionType: string = 'view') => {
    JourneyTracker.trackAction(`feature_${interactionType}`, { feature });
    
    if (typeof window !== 'undefined' && window.omnisend) {
      window.omnisend.push(['track', 'feature_interest_shown', {
        feature_name: feature,
        interaction_type: interactionType,
        session_duration: JourneyTracker.getSessionDuration(),
        page: window.location.pathname
      }]);
    }
  },

  // Get current journey data
  getCurrentJourney: (): UserJourney | null => {
    const currentJourneyStr = sessionStorage.getItem('current_journey');
    return currentJourneyStr ? JSON.parse(currentJourneyStr) : null;
  },

  // Get session duration in seconds
  getSessionDuration: (): number => {
    const journey = JourneyTracker.getCurrentJourney();
    if (!journey) return 0;
    return Math.round((Date.now() - journey.startTime) / 1000);
  },

  // End journey (on successful conversion or session end)
  endJourney: (outcome: 'trial_started' | 'purchase_completed' | 'abandoned' | 'session_timeout') => {
    const journey = JourneyTracker.getCurrentJourney();
    if (!journey) return;
    
    const finalJourney = {
      ...journey,
      endTime: Date.now(),
      outcome,
      totalDuration: JourneyTracker.getSessionDuration()
    };
    
    // Store completed journey for analysis
    const completedJourneys = JSON.parse(localStorage.getItem('completed_journeys') || '[]');
    completedJourneys.push(finalJourney);
    
    // Keep only last 10 journeys to avoid storage bloat
    if (completedJourneys.length > 10) {
      completedJourneys.shift();
    }
    
    localStorage.setItem('completed_journeys', JSON.stringify(completedJourneys));
    
    // Track journey completion
    if (typeof window !== 'undefined' && window.omnisend) {
      window.omnisend.push(['track', 'journey_completed', {
        session_id: journey.sessionId,
        outcome,
        total_duration: finalJourney.totalDuration,
        steps_count: journey.steps.length,
        user_type: journey.userType,
        device: journey.device,
        source: journey.source
      }]);
    }
    
    // Clear current journey
    sessionStorage.removeItem('current_journey');
    sessionStorage.removeItem('page_start_time');
  },

  // Get journey insights for personalization
  getJourneyInsights: () => {
    const journey = JourneyTracker.getCurrentJourney();
    const completedJourneys = JSON.parse(localStorage.getItem('completed_journeys') || '[]');
    
    return {
      currentJourney: journey,
      pastJourneys: completedJourneys,
      userBehavior: {
        averageSessionDuration: completedJourneys.reduce((acc: number, j: any) => acc + (j.totalDuration || 0), 0) / Math.max(completedJourneys.length, 1),
        mostVisitedPages: journey?.steps.reduce((acc: any, step) => {
          acc[step.page] = (acc[step.page] || 0) + 1;
          return acc;
        }, {}) || {},
        conversionRate: completedJourneys.filter((j: any) => j.outcome === 'trial_started' || j.outcome === 'purchase_completed').length / Math.max(completedJourneys.length, 1)
      }
    };
  }
};

// Auto-initialize journey tracking on page load
if (typeof window !== 'undefined') {
  // Track page changes for SPAs
  let currentPath = window.location.pathname;
  
  const handleRouteChange = () => {
    const newPath = window.location.pathname;
    if (newPath !== currentPath) {
      JourneyTracker.trackPageSequence(newPath);
      currentPath = newPath;
    }
  };
  
  // Listen for navigation events
  window.addEventListener('popstate', handleRouteChange);
  
  // For programmatic navigation, we'll need to call trackPageSequence manually
  // This will be integrated into the routing components
}

export default JourneyTracker;